import React, { useMemo, useState, useEffect } from 'react';
import { Plus, CalendarX2 } from 'lucide-react';
import { Modal } from './ui';
import { useIsMobile } from '../hooks/useResponsive';
import { pad2, toDateStr, giornoLabel } from './content';

/**
 * Calendario mensile condiviso (Calendario, PED, PED Storie).
 *
 * Desktop: griglia classica, fino a 3 elementi per giorno + "altri N" (apre il giorno).
 * Telefono: mese compatto con pallini colorati; toccando un giorno la sua agenda
 * compare sotto la griglia (niente testo minuscolo dentro celle strette).
 *
 * items: [{ id, date:'YYYY-MM-DD', time:'HH:MM'|'' , title, color, sub?, selected?, dimmed?, draggable? }]
 */
const DOW = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MAX = 3;

export function AgendaList({ items, onItemClick, empty = 'Niente in programma.' }) {
  if (!items.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 4px', color: 'var(--text-dim)', fontSize: 13 }}>
        <CalendarX2 size={16} /> {empty}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map(it => (
        <button key={it.id} className="eden-agenda-item" style={{ '--c': it.color, outline: it.selected ? '2px solid var(--green)' : 'none', opacity: it.dimmed ? 0.5 : 1 }}
          onClick={() => onItemClick(it)}>
          <span className="bar" />
          <span className="time">{it.time || '—'}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</span>
            {it.sub && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.sub}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function MonthCalendar({ month, items, onItemClick, onAdd, onMove, addLabel = 'Aggiungi', legend }) {
  const isMobile = useIsMobile();
  const year = month.getFullYear();
  const mon = month.getMonth();
  const today = toDateStr(new Date());
  const firstWd = (new Date(year, mon, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const dayStr = (d) => `${year}-${pad2(mon + 1)}-${pad2(d)}`;

  const [dragId, setDragId] = useState(null);
  const [overDay, setOverDay] = useState(null);
  const [dayOpen, setDayOpen] = useState(null);           // desktop: giorno aperto in modale
  const defaultSel = today.slice(0, 7) === `${year}-${pad2(mon + 1)}` ? today : dayStr(1);
  const [selDay, setSelDay] = useState(defaultSel);        // mobile: giorno selezionato
  useEffect(() => { setSelDay(defaultSel); }, [defaultSel]);

  const byDay = useMemo(() => {
    const m = {};
    for (const it of items) { if (it.date) (m[it.date] = m[it.date] || []).push(it); }
    Object.values(m).forEach(l => l.sort((a, b) => (a.time || '99').localeCompare(b.time || '99')));
    return m;
  }, [items]);

  const cells = [];
  for (let i = 0; i < firstWd; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  /* ── Telefono ── */
  if (isMobile) {
    const list = byDay[selDay] || [];
    return (
      <div>
        <div className="eden-cal is-compact">
          <div className="eden-cal-dow">{DOW.map(g => <span key={g}>{g.charAt(0)}</span>)}</div>
          <div className="eden-cal-grid">
            {cells.map((d, i) => {
              if (d === null) return <div key={`x${i}`} className="eden-cal-cell is-empty" />;
              const ds = dayStr(d);
              const its = byDay[ds] || [];
              const cls = ['eden-cal-cell', ds === today && 'is-today', ds === selDay && 'is-sel'].filter(Boolean).join(' ');
              return (
                <div key={ds} className={cls} onClick={() => setSelDay(ds)} role="button" tabIndex={0}
                  aria-label={`${giornoLabel(ds, { long: true })}: ${its.length} elementi`}
                  onKeyDown={e => { if (e.key === 'Enter') setSelDay(ds); }}>
                  <span className="eden-cal-num" style={{ pointerEvents: 'none' }}>{d}</span>
                  <span className="eden-cal-dots">
                    {its.slice(0, 3).map(it => <i key={it.id} style={{ '--c': it.color }} />)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 650, color: 'var(--text)', textTransform: 'capitalize' }}>
              {giornoLabel(selDay, { long: true })}
              <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-dim)', marginLeft: 8, textTransform: 'none' }}>{list.length || ''}</span>
            </div>
            {onAdd && <button className="eden-btn is-sm" onClick={() => onAdd(selDay)}><Plus size={14} />{addLabel}</button>}
          </div>
          <AgendaList items={list} onItemClick={onItemClick} />
        </div>
        {legend && <Legend legend={legend} />}
      </div>
    );
  }

  /* ── Desktop ── */
  const dayList = dayOpen ? (byDay[dayOpen] || []) : [];
  return (
    <div>
      <div className="eden-cal">
        <div className="eden-cal-dow">{DOW.map(g => <span key={g}>{g}</span>)}</div>
        <div className="eden-cal-grid">
          {cells.map((d, i) => {
            const weekend = (i % 7) >= 5;
            if (d === null) return <div key={`x${i}`} className="eden-cal-cell is-out" />;
            const ds = dayStr(d);
            const its = byDay[ds] || [];
            const cls = ['eden-cal-cell', ds === today && 'is-today', weekend && 'is-weekend', overDay === ds && 'is-over'].filter(Boolean).join(' ');
            return (
              <div key={ds} className={cls}
                onDragOver={onMove ? (e) => { e.preventDefault(); if (overDay !== ds) setOverDay(ds); } : undefined}
                onDragLeave={onMove ? () => setOverDay(o => (o === ds ? null : o)) : undefined}
                onDrop={onMove ? () => { const it = items.find(x => x.id === dragId); if (it) onMove(it, ds); setDragId(null); setOverDay(null); } : undefined}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <button className="eden-cal-num" onClick={() => setDayOpen(ds)} title="Apri il giorno">{d}</button>
                </div>
                {onAdd && (
                  <button className="eden-cal-add" onClick={() => onAdd(ds)} title={addLabel} aria-label={`${addLabel} il ${d}`}><Plus size={14} /></button>
                )}
                {its.slice(0, MAX).map(it => (
                  <button key={it.id} className={`eden-cal-item${it.selected ? ' is-sel' : ''}${it.dimmed || dragId === it.id ? ' is-dim' : ''}`}
                    style={{ '--c': it.color }} title={`${it.time ? it.time + ' · ' : ''}${it.title}${it.sub ? ' · ' + it.sub : ''}`}
                    draggable={!!onMove && it.draggable !== false}
                    onDragStart={() => setDragId(it.id)} onDragEnd={() => { setDragId(null); setOverDay(null); }}
                    onClick={() => onItemClick(it)}>
                    {it.time && <span className="t">{it.time}</span>}
                    <span className="l">{it.title}</span>
                  </button>
                ))}
                {its.length > MAX && (
                  <button className="eden-cal-more" onClick={() => setDayOpen(ds)}>altri {its.length - MAX}</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {legend && <Legend legend={legend} />}

      <Modal open={!!dayOpen} onClose={() => setDayOpen(null)} width={440}
        title={dayOpen ? giornoLabel(dayOpen, { long: true }).replace(/^./, c => c.toUpperCase()) : ''}>
        <AgendaList items={dayList} onItemClick={(it) => { setDayOpen(null); onItemClick(it); }} />
        {onAdd && dayOpen && (
          <button className="eden-btn" style={{ width: '100%', marginTop: 12, borderStyle: 'dashed' }}
            onClick={() => { const d = dayOpen; setDayOpen(null); onAdd(d); }}>
            <Plus size={15} /> {addLabel} in questo giorno
          </button>
        )}
      </Modal>
    </div>
  );
}

function Legend({ legend }) {
  return (
    <div className="eden-scroll-row" style={{ gap: 14, marginTop: 12, padding: '0 2px' }}>
      {legend.map(l => (
        <span key={l.label} className="eden-fmt" style={{ '--fmt': l.color, color: 'var(--text-dim)', fontWeight: 500 }}>{l.label}</span>
      ))}
    </div>
  );
}
